import { LOCAL_EN, THEME_VARIABLE } from '@/config';
import { Flex, Modal, Typography, Tag, Row, Col, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { AddIcon, CalendarOutline, CheckOutline, CloseCircle, DownIcon } from '../Icons';
import { useFilterOrderStore } from '@/stores';
import dayjs, { Dayjs } from 'dayjs';
import { SelectiveTag } from '../SelectiveTag';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { OrderFilters } from '@/stores/useFilterOrderStore';
import { Heading } from '..';

const { CheckableTag } = Tag;
const { Text } = Typography;

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  handleFilterOrders: (filters: OrderFilters) => void;
}

const FiltersModal: React.FC<ModalProps> = ({ visible, onClose, handleFilterOrders }) => {
  const { t, i18n } = useTranslation();
  const { createdAtFilter, orderTypeFilter, carrierFilter } = useFilterOrderStore();

  const [createdAtState, setCreatedAtState] = useState(createdAtFilter);
  const [orderTypeState, setOrderTypeState] = useState(orderTypeFilter);
  const [carrierState, setCarrierState] = useState(carrierFilter);

  const orderTypeList: OrderTypeEnum[] = [
    OrderTypeEnum.NORMAL,
    OrderTypeEnum.RETURN,
    OrderTypeEnum.EXCHANGE,
  ];

  // Static carrier list - will be replaced with API call later
  const carrierList: string[] = [
    'Sandoog Express',
    'FastShip Logistics',
    'QuickDelivery Co.',
    'SpeedPost',
    'GlobalCarrier',
  ];

  const areEnumSetsEqual = (a?: OrderTypeEnum[], b?: OrderTypeEnum[]): boolean => {
    const setA = new Set(a ?? []);
    const setB = new Set(b ?? []);

    return setA.size === setB.size && [...setA].every((item) => setB.has(item));
  };

  const areDateRangesEqual = (
    a: [dayjs.Dayjs, dayjs.Dayjs] | undefined,
    b: [dayjs.Dayjs, dayjs.Dayjs] | undefined,
  ) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a[0].isSame(b[0]) && a[1].isSame(b[1]);
  };

  const areStringArraysEqual = (a?: string[], b?: string[]): boolean => {
    const setA = new Set(a ?? []);
    const setB = new Set(b ?? []);

    return setA.size === setB.size && [...setA].every((item) => setB.has(item));
  };

  useEffect(() => {
    if (!areDateRangesEqual(createdAtState, createdAtFilter)) setCreatedAtState(createdAtFilter);
    if (!areEnumSetsEqual(orderTypeState, orderTypeFilter)) setOrderTypeState(orderTypeFilter);
    if (!areStringArraysEqual(carrierState, carrierFilter)) setCarrierState(carrierFilter);
  }, [createdAtFilter, orderTypeFilter, carrierFilter]);

  const handleOrderTypeFilterChange = (orderType: string, checked: boolean) => {
    const currentList = orderTypeState ?? [];

    const updatedList = checked
      ? Array.from(new Set([...currentList, orderType])) // Add if not exists
      : currentList.filter((item) => item !== orderType); // Remove if exists

    setOrderTypeState(updatedList as OrderTypeEnum[]);
  };

  const handleCarrierFilterChange = (carrier: string, checked: boolean) => {
    const currentList = carrierState ?? [];

    const updatedList = checked
      ? Array.from(new Set([...currentList, carrier])) // Add if not exists
      : currentList.filter((item) => item !== carrier); // Remove if exists

    setCarrierState(updatedList);
  };

  const onClear = () => {
    setOrderTypeState([]);
    setCarrierState([]);
    setCreatedAtState(undefined);
  };

  const onSubmit = () => {
    handleFilterOrders({
      createdAtFilter: createdAtState,
      orderTypeFilter: orderTypeState,
      carrierFilter: carrierState,
    });
    onClose();
  };

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        <Flex
          className='font-bold uppercase text-lg leading-5 text-customTunaPrimary'
          style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '20px' }}
        >
          {t('filters')}
        </Flex>
      }
      width={760}
      centered
      open={visible}
      onCancel={onClose}
      onClose={onClose}
      footer={
        <div className='flex justify-end w-full gap-3'>
          <button
            type='button'
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
            onClick={onClear}
          >
            <CloseCircle width={16} height={16} />
            {String(t('clear_all'))}
          </button>
          <button
            type='button'
            className='flex items-center gap-1.5 h-9 px-5 min-w-[120px] rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
            onClick={onSubmit}
          >
            {String(t('apply'))}
          </button>
        </div>
      }
    >
      <Flex vertical gap={'2rem'} className='my-5'>
        <Flex vertical gap={'1rem'}>
          <Text className='font-normal text-base leading-[20.48px] text-customTunaPrimary capitalize'>
            {t('by_order_type')}
          </Text>
          <Row gutter={[8, 8]}>
            {orderTypeList.map((orderType) => {
              return (
                <Col key={orderType}>
                  <SelectiveTag
                    selectedTags={orderTypeState as string[]}
                    tagKey={orderType}
                    tagLabel={t(orderType.toLocaleLowerCase())}
                    handleChange={handleOrderTypeFilterChange}
                  />
                </Col>
              );
            })}
          </Row>
        </Flex>
        {/* <Flex vertical gap={'1rem'}>
          <Text className='font-normal text-base leading-[20.48px] text-customTunaPrimary capitalize'>
            {t('by_carrier')}
          </Text>
          <Row gutter={[8, 8]}>
            {carrierList.map((carrier) => {
              return (
                <Col key={carrier}>
                  <SelectiveTag
                    selectedTags={carrierState as string[]}
                    tagKey={carrier}
                    tagLabel={carrier}
                    handleChange={handleCarrierFilterChange}
                  />
                </Col>
              );
            })}
          </Row>
        </Flex> */}
        <Flex vertical gap={'1rem'}>
          {/* Date Picker with Prefix Icon */}
          <Text className='font-normal text-base leading-[20.48px] text-customTunaPrimary capitalize'>
            {t('by_created_at_date')}
          </Text>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Flex className='relative inline-block w-full'>
                {/* <span
                  className={` ${i18n.language === LOCAL_EN ? 'left-3' : 'right-3'} absolute  top-1/2 transform -translate-y-1/2 z-10 flex items-center`}
                >
                  <CalendarOutline width={14} height={14} color={THEME_VARIABLE.customGrayShade4} />
                </span> */}

                {/* //  ${i18n.language === LOCAL_EN ? 'pl-10' : 'pr-10'} */}
                <DatePicker.RangePicker
                  className={`w-full custom-date-picker  border border-solid border-customGrayShade5 rounded-lg h-[46px] 
                     text-center font-bold`}
                  format='YYYY-MM-DD'
                  placeholder={[t('start_date'), t('end_date')]}
                  showNow
                  onChange={(dates) => setCreatedAtState((dates as [Dayjs, Dayjs]) ?? undefined)}
                  maxDate={dayjs()}
                  value={createdAtState}
                />
              </Flex>
            </Col>
          </Row>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default FiltersModal;
